<template>
  <div>
    <AppStoreBanner
      :shouldShown="shouldShownAppStoreBanner"
      @close="closeAppStoreBanner"
    />
    <div
      class="search-page"
      :class="shouldShownAppStoreBanner ? 'app-store-banner-space' : null"
    >
      <div class="search-header">
        <div ref="searchHeader" class="search-header-inner bg-white">
          <!-- ノッチ対応用DOM -->
          <div class="search-header-notch"></div>
          <OrderTab
            :orderType="orderType"
            :existsLimitedOrder="existsLimitedOrders"
            :existsIndividualOrder="existsIndividualOrders"
          />
          <div class="search-header-items">
            <DesiredWorkScheduleButton v-if="isLogin" class="schedule-button" />
            <FilterButton
              id="tooltippopup-to-filter-button"
              class="mr-3"
              :filterCount="filterCount"
              @click-filter="
                $router.push({
                  name: 'SearchCondition',
                  query: { orderType }
                })
              "
            />
            <DistanceSortButton
              :isSortApplied="isDistanceSortApplied"
              @click-sort="toggleDistanceSortKey"
            />
          </div>
          <OrderDatePicker
            :dateList="dateList"
            :selectedDate="selectedDate"
            @select="setSelectedDate"
          />
        </div>
      </div>

      <!-- toaster -->
      <BToaster
        name="search-toaster-top"
        class="search-toaster position-fixed"
      />

      <!-- AD -->
      <Ad adSlot="search_public_order" class="ad-slot-search" />
      <Ad adSlot="modal" />

      <!-- 吹き出し -->
      <TooltipPopup
        position="bottom"
        targetSelector="#tooltippopup-to-filter-button"
      >
        <div>
          お仕事探すなら<TooltipHighlightText>絞り込み</TooltipHighlightText>が便利！
        </div>
      </TooltipPopup>

      <div
        v-touch:swipe.left="swipeLeftHandler"
        v-touch:swipe.right="swipeRightHandler"
        class="w-100 container pull-to-refresh-ios"
      >
        <PullToRefresh
          @pull="reloadOrder"
          :isShowingPullToRefreshLoading="isShowingPullToRefreshLoading"
        >
          <OrderCardSimple
            v-for="(order, index) in orderList"
            :key="'orderList/' + index"
            :order="order"
            :isLogin="isLogin"
            :isRegisteredWatchLater="isRegisteredWatchLater(order)"
            :id="createOrderIdForElement((order || {}).id)"
            class="my-2 mx-2"
            :evaluation="getEvaluation(order)"
            @card-click="onCardClick"
            @change-watch-later="onWatchLaterChange(order)"
          />
          <div class="infinite-loading-container">
            <p
              v-if="orderList.length > 0"
              class="p-3 bg-white text-14 text-center"
            >
              ここが最後です
            </p>
            <p
              v-if="orderList.length === 0"
              class="p-3 bg-white text-14 text-center"
            >
              条件に一致する仕事はありません
            </p>
          </div>
        </PullToRefresh>
      </div>
      <AppFooter />
      <BlockBack />
      <BModal v-model="needsGPSModal" title="位置情報について" centered>
        <template #default>
          最寄りのお仕事を表示するため、端末の位置情報を使用します。
          お使いの端末の設定から権限を許可してください。<br /><br />
          なお、絞込機能で表示するお仕事を絞り込みすることも可能です。
        </template>
        <template #modal-footer="{ ok }">
          <BButton size="sm" variant="text" @click="ok"> OK </BButton>
          <BButton
            v-if="isCordova"
            size="sm"
            variant="secondary"
            @click="openSetting"
          >
            設定を開く
          </BButton>
        </template>
      </BModal>
      <FirstBootSurveyModal
        :show="showFirstBootSurvey"
        @close="onFirstBootSurveyRegistered"
      />
      <JobChangeModal v-if="shouldAskJobChange" />
      <ApplicationReminderModal
        data-testid="application-reminder-modal"
        :applicationToRemind="applicationToRemind"
        @close-hook="closeHookApplicationReminderModal"
      />
    </div>
    <AppSatisfactionModal
      id="app-review-modal"
      :shouldShow="shouldShowAppSatisfactionModal"
      :answeredTiming="answeredTiming"
      @close="closeAppReviewModal"
    />
    <WorkerServiceReSettingModal
      :show="needsWorkerServiceReSettingModal"
      @submit="submitWorkerServiceReSetting"
    />
  </div>
</template>

<script lang="ts">
import { updateIfNewerVersionExists } from "@/app-updator";
import { accountService } from "@/app/services/AccountService";
import { applicationService } from "@/app/services/ApplicationService";
import { appStoreBannerService } from "@/app/services/AppStoreBannerService";
import {
  AnalyzeWatchLaterEvent,
  eventService
} from "@/app/services/EventService";
import { officeService } from "@/app/services/OfficeService";
import { orderService } from "@/app/services/OrderService";
import DesiredWorkScheduleButton from "@/components/atoms/button/DesiredWorkScheduleButton.vue";
import FilterButton from "@/components/atoms/button/FilterButton.vue";
import AppStoreBanner from "@/components/atoms/other/AppStoreBanner.vue";
import TooltipHighlightText from "@/components/atoms/other/TooltipHighlightText.vue";
import PullToRefresh from "@/components/molecules/other/PullToRefresh.vue";
import TooltipPopup from "@/components/molecules/other/TooltipPopup.vue";
import WorkerServiceReSettingModal from "@/components/organisms/accounts/WorkerServiceReSettingModal.vue";
import Ad from "@/components/organisms/common/Ad.vue";
import AppFooter from "@/components/organisms/common/AppFooter.vue";
import BlockBack from "@/components/organisms/common/BlockBack.vue";
import ApplicationReminderModal from "@/components/organisms/orders/ApplicationReminderModal/ApplicationReminderModal.vue";
import AppSatisfactionModal from "@/components/organisms/orders/AppSatisfactionModal/AppSatisfactionModal.vue";
import DistanceSortButton from "@/components/organisms/orders/DistanceSortButton.vue";
import FirstBootSurveyModal from "@/components/organisms/orders/FirstBootSurveyModal.vue";
import JobChangeModal from "@/components/organisms/orders/JobChangeModal.vue";
import OrderCardSimple from "@/components/organisms/orders/OrderCardSimple.vue";
import OrderDatePicker from "@/components/organisms/orders/OrderDatePicker.vue";
import OrderTab from "@/components/organisms/orders/OrderTab.vue";
import ApplicationStatus from "@/models/applicationStatus";
import { captureException } from "@/plugins/sentry";
import { applicationStore } from "@/store/application";
import { authStore } from "@/store/auth";
import { deviceModule } from "@/store/device";
import { masterStore } from "@/store/master";
import { orderStore } from "@/store/order";
import { Application } from "@/types/order/Application";
import { SlimOrder } from "@/types/order/Order";
import { getCreditScoreRecoveryDateForDisplay } from "@/utils/creditScore";
import {
  addDays,
  addHours,
  addWeeks,
  eachDayOfInterval,
  formatFromUnixTimestamp,
  isToday,
  isTomorrow,
  now,
  parseISO,
  startOfDay,
  startOfToday,
  startOfTomorrow,
  subDays
} from "@/utils/date";
import { openApplicationSettings } from "@/utils/diagnostic";
import { getFcmToken } from "@/utils/firebase";
import { getCurrentPositionWrapper } from "@/utils/gps";
import { useLoginModal } from "@/utils/modal";
import { isNotNullOrUndefined } from "@/utils/nullUndefinedCheck";
import { showErrorModal } from "@/utils/showErrorModal";
import { split } from "@/utils/stringUtil";
import "mobile-pull-to-refresh/dist/styles/ios/style.css";
import { h } from "vue";
import {
  Component,
  Hook,
  Prop,
  Vue,
  Watch,
  toNative
} from "vue-facing-decorator";
import { NavigationGuardNext, RouteLocation } from "vue-router";

const limit = 20;

@Component({
  components: {
    AppStoreBanner,
    AppFooter,
    FilterButton,
    DistanceSortButton,
    OrderCardSimple,
    BlockBack,
    PullToRefresh,
    OrderDatePicker,
    FirstBootSurveyModal,
    JobChangeModal,
    AppSatisfactionModal,
    ApplicationReminderModal,
    OrderTab,
    Ad,
    DesiredWorkScheduleButton,
    TooltipPopup,
    TooltipHighlightText,
    WorkerServiceReSettingModal
  }
})
class Search extends Vue {
  @Prop({
    default: "public",
    type: String
  })
  orderType!: string;

  isInitialDataFetched = false;

  // 位置情報のモーダルの展開
  needsGPSModal = false;
  // 初回アンケートの展開
  needsFirstBootSurveyModal = false;

  // プロダクト（アプリ）切り替えした場合に業務カテゴリが不整合になる場合があるため、
  // それを解消するため業務カテゴリを選択させるモーダルを開く
  // 例）普段、薬剤師アプリを使っているワーカー（業務カテゴリ:3）が、介護・看護アプリを開く
  needsWorkerServiceReSettingModal = false;

  // pulltorefresh関連の制御する変数
  isShowingPullToRefreshLoading = true;

  // InfinityScrollの制御
  isInfinityScrollLoading = false;
  allDataLoaded = false;

  toastTimer: number | null = null;

  currentPosition: GeolocationPosition | null = null;

  // アプリ評価モーダルを表示するべきか
  shouldShowAppSatisfactionModal = false;
  // 勤務完了タイミング
  answeredTiming: string | null = null;
  shownPages = 1;

  // あとで見るリスト
  watchLaterOrderList = orderStore.watchLaterOrderList;

  loginModal = useLoginModal();
  // 前日確認
  // 前日確認するべき応募
  applicationToRemind: Application | null = null;
  // 前日確認モーダル用タイマー
  applicationModalTimer: number | null = null;

  existsLimitedOrders = false;
  existsIndividualOrders = false;

  get orderList(): SlimOrder[] {
    const l = orderStore.orderList || [];
    return l.slice(0, limit * this.shownPages);
  }
  get evaluations() {
    return orderStore.evaluations || {};
  }
  get officeIds() {
    return [...new Set(orderStore.orderList?.map(o => o.office.id))];
  }
  get isLogin() {
    return authStore.existToken;
  }
  get isNursing() {
    return authStore.isNursing;
  }
  get hasRegisteredProfile() {
    return authStore.user?.hasRegisteredProfile;
  }
  get hasRegisteredPersonalInfo() {
    return authStore.user?.hasRegisteredPersonalInfo;
  }
  get isCordova() {
    // @ts-ignore
    return !!window.cordova;
  }
  get isDistanceSortApplied() {
    return orderStore.orderSortKey === "distance";
  }
  get selectedDateStr() {
    return orderStore.orderSearchQuery?.dates || this.defaultSelectedDateStr;
  }
  get selectedDate() {
    return startOfDay(parseISO(this.selectedDateStr));
  }
  get filterCount() {
    const query = orderStore.orderSearchQuery;
    if (!query) return 0;
    return (
      split(query.prefectures).length +
      split(query.localities).length +
      split(query.service_types).length +
      (split(query.qualifications).length > 0 ? 1 : 0) +
      split(query.shift_types).length +
      split(query.duration_types).length +
      (isNotNullOrUndefined(query.begin_hour) ||
      isNotNullOrUndefined(query.end_hour)
        ? 1
        : 0) +
      (query.free_word ? 1 : 0) +
      (this.isLogin && this.isNursing && query.only_nursing ? 1 : 0) +
      (query.exclude_career ? 1 : 0) +
      (query.hourly_wage ? 1 : 0) +
      split(query.recommendation_points).length +
      (query.without_bath_assistance ? 1 : 0) +
      (query.with_shuttle_driver_duties ? 1 : 0) +
      (query.service_started_within_last_five_years ? 1 : 0) +
      (query.can_commute_by_car ? 1 : 0)
    );
  }
  get defaultSelectedDate() {
    const today = startOfToday();
    return addDays(today, 7);
  }
  get defaultSelectedDateStr() {
    return formatFromUnixTimestamp(this.defaultSelectedDate, "yyyy-MM-dd");
  }
  get showFirstBootSurvey() {
    return this.needsFirstBootSurveyModal;
  }
  get shouldAskJobChange() {
    if (!this.isLogin) {
      return false;
    }
    const user = authStore.user;
    // 他のモーダルが表示されている場合、転職意欲を聞かない。
    if (this.needsGPSModal || this.needsFirstBootSurveyModal) return false;
    if (!user) return false;
    if (!user.jobChangeUpdatedAt) return true;
    // 転職意欲に更新があってから、30日以上経過してい場合のみ、転職意欲を聞く。
    return subDays(now(), 30) > parseISO(user.jobChangeUpdatedAt);
  }

  get displayCreditScoreRecoveryDate() {
    if (authStore.user === null) {
      return null;
    }
    return getCreditScoreRecoveryDateForDisplay(authStore.user);
  }

  get shouldShownAppStoreBanner() {
    return appStoreBannerService.shouldShownBanner();
  }

  get searchLocation() {
    if (
      this.hasRegisteredPersonalInfo ||
      (!this.currentPosition && !authStore.firstBootSurveyV2)
    ) {
      return null;
    }
    if (this.currentPosition) {
      return {
        latitude: this.currentPosition.coords.latitude,
        longitude: this.currentPosition.coords.longitude
      };
    }
    return {
      latitude: authStore.firstBootSurveyV2?.latitude,
      longitude: authStore.firstBootSurveyV2?.longitude
    };
  }

  get workCount() {
    return authStore.user?.workCount || 0;
  }

  closeAppStoreBanner() {
    appStoreBannerService.haveAlreadySeenBanner();
  }

  // 画面上部に出す日付の一覧
  get dateList() {
    const today = startOfToday();
    const end = addWeeks(subDays(today, 1), 52);
    return eachDayOfInterval(today, end);
  }

  async created() {
    // この画面であればリロードしてもユーザに影響はないため、
    // アプリのバージョンが新しくなっていたら更新する。非同期で実行
    updateIfNewerVersionExists().then();

    this.applicationModalTimer = window.setInterval(
      this.showApplicationReminderModal,
      1000
    );

    if (orderStore.orderList === undefined) {
      // orderListがundefinedの場合はorderListがreactiveになっていないため、一度空を設定し
      // 画面自体をリロードすることで、orderListがreactiveになるようにする。
      orderStore.addOrderList([]);
      location.reload();
      return;
    }
    try {
      this.$loading.show();

      // ワーカー基本情報入力済みの場合
      if (this.hasRegisteredPersonalInfo) {
        this.reSettingWorkerServiceByProduct();
        return;
      }

      // 初回アンケート回答済みの場合
      if (await this.isAnsweredFirstBootSurvey()) {
        return;
      }

      // 上記以外の場合
      this.needsFirstBootSurveyModal = true; // 初回アンケート表示
    } catch (e) {
      showErrorModal(this, e as Error);
    } finally {
      this.$loading.hide();
    }
  }

  async reSettingWorkerServiceByProduct() {
    const includeWorkerServiceInProduct = authStore.product.workerServices.some(
      ws => ws.id === authStore.user?.workerService
    );
    if (includeWorkerServiceInProduct) {
      // ユーザが設定している業務カテゴリが、今アクセス中のプロダクトが取り扱える業務カテゴリに含まれる場合
      // 何もしない
    } else {
      // ユーザが設定している業務カテゴリが、今アクセス中のプロダクトが取り扱える業務カテゴリに含まれない場合（＝以前は違うアプリを利用していた）
      // 業務カテゴリを再設定するモーダルを開く（ただし、プロダクトが取り扱える業務カテゴリが1つしかない場合は自動で設定する）
      if (authStore.product.workerServices.length === 1) {
        await this.updateWorkerService(authStore.product.workerServices[0].id);
      } else {
        this.needsWorkerServiceReSettingModal = true;
      }
    }
  }

  async updateWorkerService(workerServiceId: number) {
    await accountService.migrateWorkerService(workerServiceId);
    await accountService.updateUserData();
    orderStore.clearQualificationsFromSearchQuery();
  }

  async submitWorkerServiceReSetting(workerServiceId: number) {
    try {
      this.$loading.show();

      await this.updateWorkerService(workerServiceId);
      this.needsWorkerServiceReSettingModal = false;
    } catch (e) {
      showErrorModal(this, e as Error);
    } finally {
      this.$loading.hide();
    }
  }

  async mounted() {
    window.addEventListener("scroll", this.onScroll);
    // 位置の復元
    this.scrollToOrder(Number(this.$route.query.focusedOrderId as string));

    // アプリ評価モーダルを表示するか
    if (this.isLogin) {
      const {
        shouldShowAppSatisfaction,
        answeredTiming
      } = await accountService.shouldShowAppReviewModal();
      // Androidの場合のみアプリ評価モーダルを表示する
      this.shouldShowAppSatisfactionModal =
        shouldShowAppSatisfaction && deviceModule.isAndroid;
      this.answeredTiming = answeredTiming;

      // 前回のトースト表示後1時間以内は何もしない
      if (
        orderStore.shownUnsendAccountVerificationToastTime !== null &&
        addHours(orderStore.shownUnsendAccountVerificationToastTime, 1) > now()
      ) {
        return;
      }

      const modifiedStatuses = await accountService.fetchModifiedStatus();

      if (
        !this.hasRegisteredProfile ||
        modifiedStatuses.isPersonalInfoModified ||
        modifiedStatuses.isAvatarModified ||
        modifiedStatuses.isIdentifierModified
      ) {
        this.$bvToast.toast(
          [
            "アカウント認証申請が完了していません",
            "マイページより申請をお願いいたします"
          ].map(text => h("p", { class: "text-white" }, text)),
          {
            id: "unsend-account-verification",
            toastClass: "toast-custom",
            toaster: "search-toaster-top",
            autoHideDelay: 5000,
            to: "/mypage"
          }
        );
        orderStore.setShownUnsendAccountVerificationToastTime(now());
      }
    }
  }

  createOrderIdForElement(orderId?: number) {
    return `order-${orderId}`;
  }

  scrollToOrder(orderId: number) {
    if (!orderId) return;
    this.$nextTick(async () => {
      if (!orderStore.orderList) return;
      const searchHeaderRef = this.$refs.searchHeader as Element;
      const index = orderStore.orderList.findIndex(
        order => order.id === orderId
      );
      if (index === -1) return;
      this.shownPages += Math.ceil(index / limit);
      await this.$nextTick();
      const el = document.getElementById(this.createOrderIdForElement(orderId));
      if (!el || !searchHeaderRef) return;

      const cardRect = el.getBoundingClientRect();
      const headerRect = searchHeaderRef.getBoundingClientRect();
      window.scrollTo(0, cardRect.y - headerRect.height);
    });
  }

  async onScroll() {
    // 上部の日付ボタンで検索画面間で遷移する際、初期描画時にonScrollが呼ばれ、かつ、一番下までスクロールした扱いになる。だが、その時点では初期データ取得自体が完了してないので無視する
    if (!this.isInitialDataFetched) return;

    // 一番下までスクロールしたら次のデータを取得する
    if (
      !this.isInfinityScrollLoading &&
      !this.allDataLoaded &&
      Math.ceil(window.innerHeight + window.scrollY) >= // androidは小数点が入るがoffsetHeightは整数なのでceilする
        document.body.offsetHeight
    ) {
      try {
        this.isInfinityScrollLoading = true;
        this.$loading.show();
        this.shownPages += 1;
        await this.fetchOrders();
      } catch (e) {
        showErrorModal(this, e as Error);
      } finally {
        this.isInfinityScrollLoading = false;
        this.$loading.hide();
      }
    }
  }

  @Hook
  beforeRouteEnter(
    to: RouteLocation,
    from: RouteLocation,
    next: NavigationGuardNext
  ) {
    next(vm => {
      const self = vm as Search;
      if (from.name === "OrdersDetail") {
        self.hideToast();
        self.init(false);
      } else if (from.name === "SearchCondition") {
        self.hideToast();
        self.init(true);
      } else if (
        from.name === "MypageFinishSignup" ||
        from.name === "RegisterFinishSignup"
      ) {
        // アカウント認証申請後に案件一覧画面に遷移した場合、吹き出しポップアップを初期化して出現させる
        authStore.setClosedTooltipPopupList();
        self.init(true);
      } else {
        self.init(true);
      }
    });
  }

  hasApplication(dates: number[]) {
    const datesString = dates.map(d =>
      formatFromUnixTimestamp(d, "yyyy-MM-dd")
    );
    return applicationStore.applications
      .filter(a =>
        [
          ApplicationStatus.APPLIED,
          ApplicationStatus.DOING,
          ApplicationStatus.DONE,
          ApplicationStatus.EVALUATED
        ].includes(a.status)
      )
      .some(a => datesString.includes(a.order.date));
  }

  @Watch("selectedDate")
  onChangeSelectedDate(newValue: number) {
    if (!this.isLogin) {
      return;
    }
    if (!authStore.user) {
      return;
    }
    if (authStore.user.creditScore === 0) {
      return;
    }
    // 前回のトースト表示後1時間以内は何もしない
    if (
      orderStore.shownNotLimitApplicationToastTime !== null &&
      addHours(orderStore.shownNotLimitApplicationToastTime, 1) > now()
    ) {
      return;
    }
    // 当日・翌日にすでに応募していた場合は何もしない
    if (
      (isToday(newValue) &&
        this.hasApplication([newValue, addDays(newValue, 1)])) ||
      (isTomorrow(newValue) &&
        this.hasApplication([subDays(newValue, 1), newValue]))
    ) {
      return;
    }
    if (isToday(newValue) || isTomorrow(newValue)) {
      this.$bvToast.toast(
        `今日（${formatFromUnixTimestamp(
          startOfToday(),
          "MM/dd"
        )}）と明日（${formatFromUnixTimestamp(
          startOfTomorrow(),
          "MM/dd"
        )}）のお仕事は、応募可能な件数に関係なく応募できます`,
        {
          id: "not-limit-application",
          toastClass: "toast-custom",
          toaster: "search-toaster-top",
          autoHideDelay: 5000,
          to: "/mypage"
        }
      );
      orderStore.setShownNotLimitApplicationToastTime(now());
    }
  }

  @Watch("officeIds")
  async onChangeOfficeIds(newValue: number[]) {
    // 未ログインの場合は評価データを取得しない
    if (!this.isLogin) {
      return;
    }
    // 存在しない事業所のデータのみ追加する
    const existsOfficeIds = Object.keys(this.evaluations);
    const requestOfficeIds = newValue.filter(
      officeId => !existsOfficeIds.includes(officeId.toString())
    );
    if (requestOfficeIds.length === 0) {
      return;
    }
    // 現状足りない分の評価データを取得する
    try {
      const evaluations = await officeService.fetchEvaluations({
        officeIds: requestOfficeIds
      });
      orderStore.addEvaluations(evaluations);
    } catch (e) {
      await showErrorModal(this, e as Error);
    }
  }

  beforeUnmount() {
    window.removeEventListener("scroll", this.onScroll);
    this.hideToast();

    if (this.applicationModalTimer) {
      clearInterval(this.applicationModalTimer);
    }
  }

  // 案件一覧の表示の初期化
  async init(reloadOrder: boolean) {
    try {
      this.$loading.show();
      this.showApplicationFailedToastIfNeeded();
      this.showCreditScoreZeroToastIfNeeded();

      await this.setCurrentPosition();
      this.showGPSModalIfNeeded();

      // 選択するべき日付を計算して設定する
      const selectedDateStr = this.calculateSelectedDate();
      // 表示するべき日付が変わったことを記録
      const isChangedSelectedDate = selectedDateStr !== this.selectedDateStr;
      this.resetSelectedDate(selectedDateStr);
      // ストアから復元した結果、日付が変わらなかった場合も踏まえてここで watch 関数を強制発火させる
      if (!isChangedSelectedDate) {
        this.onChangeSelectedDate(this.selectedDate);
      }

      await Promise.all([
        this.fetchCountOfLimitedOrders(),
        this.fetchCountOfIndividualOfferOrders()
      ]);
      this.setQueryConditionForPharamacistIfNeeded();

      // 必要があればordersをリセットして再取得
      if (isChangedSelectedDate || reloadOrder) {
        this.clearOrders();
        await this.fetchOrders();
      } else {
        this.isInitialDataFetched = true;
      }
    } catch (e) {
      showErrorModal(this, e as Error);
    } finally {
      this.$loading.hide();
    }
  }

  // TODO: 薬剤師対応 - 業務カテゴリ：薬剤師をカイテクアプリで参照しなくてよくなれば消す
  // accountService.hasAnyQualification も消せる
  setQueryConditionForPharamacistIfNeeded() {
    if (
      authStore.isCaitechPharmacistProduct ||
      orderStore.hasUsedOneTimePharmacistRegistrationFilter ||
      !authStore.user ||
      // 薬剤師関連の資格を持っていない場合は絞り込み条件を追加しない
      !accountService.hasAnyQualification([
        "pharmacist", // 薬剤師
        "Insurance_pharmacist", // 保険薬剤師登録票
        "certified_pharmacist" // 認定薬剤師
      ])
    ) {
      return;
    }

    const pharmacistTargetServiceIds = masterStore.services
      .filter(
        service => service.slug === "pharmacy" || service.slug === "hospital"
      )
      .map(service => service.id);
    if (pharmacistTargetServiceIds.length === 0) return;
    // 薬剤師の場合、絞り込み条件に「サービス種別：薬局, 病院」を追加する
    // この処理はログイン後一度だけ適用される
    // TODO: この処理は薬剤師アプリリリース後、一定期間経った後に削除する
    orderStore.setSearchQuery({
      ...orderStore.orderSearchQuery,
      service_types: pharmacistTargetServiceIds.join(",")
    });
    orderStore.setHasUsedOneTimePharmacistRegistrationFilter();
  }

  /**
   * 案件一覧画面を開いたときに1番始めに表示するべき日付を計算する
   * * 以前に指定されていた日付が今日以降である場合は、その日付を返す
   * * それ以外の場合は、デフォルトの日付(7日後)を返す
   */
  calculateSelectedDate() {
    const today = startOfToday();
    // 今日以降を選択していた場合は、その日付を選択する
    if (this.selectedDate >= today) {
      return formatFromUnixTimestamp(this.selectedDate);
    }
    // 上記以外はデフォルトの日付を表示する
    return formatFromUnixTimestamp(this.defaultSelectedDate);
  }

  resetSelectedDate(dateStr: string) {
    orderStore.setSearchQuery({
      ...orderStore.orderSearchQuery,
      dates: dateStr
    });
  }

  showGPSModalIfNeeded() {
    // 位置情報を取得してもらうための注意を一度も表示したことがなければ表示する
    if (
      !this.hasRegisteredPersonalInfo && // 基本情報登録(住所入力)が完了している場合は検索に位置情報を用いない
      !this.currentPosition &&
      !orderStore.alreadyGeoLocationModalShown
    ) {
      this.needsGPSModal = true;
      orderStore.setAlreadyGeoLocationModalShown(true);
    }
  }

  isRegisteredWatchLater(order: SlimOrder) {
    return !!this.watchLaterOrderList.find(o => o.id === order.id);
  }

  async onWatchLaterChange(order: SlimOrder) {
    if (!this.isLogin) {
      const isConfirmed = await this.loginModal.showLoginModal();
      if (isConfirmed) {
        await this.$router.push({ name: "Welcome" });
        return;
      }
      return;
    }
    const isRegistered = this.isRegisteredWatchLater(order);
    if (isRegistered) {
      orderStore.removeFromWatchLaterOrderList(order.id);
    } else {
      orderStore.addToWatchLaterOrderList(order);
    }
    this.watchLaterOrderList = orderStore.watchLaterOrderList;
    this.$bvToast.toast(
      isRegistered
        ? "あとで見るリストから削除しました"
        : "あとで見るリストに登録しました",
      {
        toastClass: "toast-custom",
        toaster: "search-toaster-top",
        autoHideDelay: 2000,
        to: "/mypage/watch-later-orders"
      }
    );
    // イベントログを記録する。非同期で十分なのでawaitしない
    eventService.registerEvent(
      AnalyzeWatchLaterEvent.group,
      isRegistered
        ? AnalyzeWatchLaterEvent.types.removeOnSearchPage
        : AnalyzeWatchLaterEvent.types.addOnSearchPage,
      undefined,
      JSON.stringify({
        watchLaterCount: orderStore.watchLaterOrderList.length,
        orderId: order.id
      })
    );
  }

  reloadOrder() {
    this.isShowingPullToRefreshLoading = true;
    this.clearOrders();
    this.fetchOrders();
  }
  onFirstBootSurveyRegistered() {
    this.needsFirstBootSurveyModal = false;
    this.init(true);
    this.$bvToast.toast("選択ありがとうございました", {
      toastClass: "toast-custom",
      toaster: "search-toaster-top",
      autoHideDelay: 5000
    });
  }
  async toggleDistanceSortKey() {
    if (orderStore.orderSortKey) {
      orderStore.clearOrderSortKey();
    } else {
      orderStore.setOrderSortKey("distance");
    }
    window.scrollTo(0, 0); // スクロールが残っていると、追加のページング分まで連続で取得してしまうことがあるため初期化
    this.clearOrders();
    await this.fetchOrders();
  }

  async setCurrentPosition() {
    if (this.hasRegisteredPersonalInfo) {
      this.currentPosition = null;
      return;
    }

    // 基本情報未登録(住所未入力)の場合だけブラウザからGeoLocationを取得する
    try {
      // 位置情報を利用するために必要であればOSの権限確認ダイアログが表示される
      this.currentPosition = await getCurrentPositionWrapper(10000);
    } catch {
      // 位置情報を取得するための権限がない場合は例外が発生するがなにもしない
    }
  }

  async fetchOrders() {
    try {
      /* eslint-disable @typescript-eslint/naming-convention */
      // 案件カード一覧作成
      this.$loading.show();
      const query = orderStore.orderSearchQuery || {};
      const fetchQuery = {
        current_latitude: this.searchLocation?.latitude,
        current_longitude: this.searchLocation?.longitude,
        date__in: this.selectedDateStr,
        locality__in: query.localities,
        office__prefecture__in: query.prefectures,
        qualification__in: query.qualifications,
        service_name: !this.hasRegisteredPersonalInfo
          ? authStore.workerService.serviceName
          : undefined,
        service_type__in: query.service_types,
        shift_type__in: query.shift_types,
        duration_type__in: query.duration_types,
        only_hiring: undefined,
        only_nursing:
          this.isLogin && this.isNursing
            ? query.only_nursing === "1"
            : undefined,
        exclude_career: query.exclude_career ? true : undefined,
        begin_hour: query.begin_hour,
        end_hour: query.end_hour,
        free_word: query.free_word,
        hourly_wage: query.hourly_wage,
        recommendation_points__in: query.recommendation_points,
        without_bath_assistance: query.without_bath_assistance,
        with_shuttle_driver_duties: query.with_shuttle_driver_duties,
        can_commute_by_car: query.can_commute_by_car,
        service_started_within_last_five_years:
          query.service_started_within_last_five_years,
        sort_key: orderStore.orderSortKey ? orderStore.orderSortKey : undefined,
        limit: limit,
        offset: (this.shownPages - 1) * limit,
        // 絞りこみ機能の情報とユーザー情報の紐づけのため。検索結果には影響しない。
        user_id: this.isLogin ? authStore.user?.id : undefined
      };
      /* eslint-enable @typescript-eslint/naming-convention */
      // @ts-ignore
      const newOrders = await orderService.searchOrders(fetchQuery);
      // pulltorefreshのローディングを止める
      this.isShowingPullToRefreshLoading = false;

      if (newOrders.length === 0) {
        this.allDataLoaded = true;
      }
      orderStore.addOrderList(newOrders);
      this.isInitialDataFetched = true;
    } catch (error) {
      showErrorModal(this, error as Error);

      // 不正なクエリ操作があった場合、画面をリセット
      if (
        // @ts-ignore
        error.response &&
        // @ts-ignore
        error.response.data &&
        // @ts-ignore
        (error.response.data.date_In || error.response.data.office_Prefecture)
      ) {
        this.resetSearchQuery();
        return;
      }
    } finally {
      this.$loading.hide();
    }
  }
  resetSearchQuery() {
    orderStore.clearSearchQuery();
  }
  async onCardClick(orderId: number) {
    await this.$router.push({
      name: "OrdersDetail",
      params: { orderId: `${orderId}` },
      query: {
        transitionSource: "Search"
      }
    });
  }
  isValidDate(date: number) {
    return this.dateList.includes(date);
  }
  setSelectedDate(date: number) {
    // 選択された日付がdateListに存在しない場合は何もしない
    if (!this.isValidDate(date)) return;
    // クエリとstoreを更新
    orderStore.setSearchQuery({
      ...orderStore.orderSearchQuery,
      dates: formatFromUnixTimestamp(date, "yyyy-MM-dd")
    });

    window.scrollTo(0, 0);
    this.clearOrders();
    this.fetchOrders();
  }
  clearOrders() {
    this.shownPages = 1;
    this.isInitialDataFetched = false;
    this.allDataLoaded = false;
    orderStore.clearOrderList();
    orderStore.clearEvaluations();
  }
  showApplicationFailedToastIfNeeded() {
    const applicationFailed = !!this.$route.params.applicationFailed;
    // 申し込み時にすでに案件が閉じられていた場合
    if (!applicationFailed) return;
    this.$bvToast.toast("申込出来ませんでした。", {
      toastClass: ["toast-custom", "is-full-limited"],
      toaster: "search-toaster-top",
      autoHideDelay: 5000
    });
  }

  showCreditScoreZeroToastIfNeeded() {
    if (this.displayCreditScoreRecoveryDate === null) return;
    if (!authStore.user) return;
    if (authStore.user.creditScore > 0) return;

    const vNodesMsg = h("div", {}, [
      h("p", { class: ["text-white"] }, [
        "信用スコアが 0pt のため、限定公開・指名オファー以外のお仕事には一時的に応募できません"
      ]),
      h("p", { class: ["text-white"] }, [
        `${this.displayCreditScoreRecoveryDate} に信用スコアが回復し、制限が解除されます`
      ])
    ]);

    // 重複して toast を出さないように一度非表示にして再度表示する
    const toastId = "credit-score-zero-toast";
    this.$bvToast.hide(toastId);
    // @ts-ignore
    this.$bvToast.toast([vNodesMsg], {
      id: toastId,
      toastClass: ["toast-custom", "is-full-limited"],
      toaster: "search-toaster-top",
      autoHideDelay: 5000
    });
  }

  swipeLeftHandler() {
    this.setSelectedDate(addDays(this.selectedDate, 1));
  }
  swipeRightHandler() {
    this.setSelectedDate(addDays(this.selectedDate, -1));
  }
  hideToast() {
    this.$bvToast.hide("not-limit-application");
    this.$bvToast.hide("unsend-account-verification");
    if (this.toastTimer !== null) {
      clearTimeout(this.toastTimer);
      this.$bvToast.hide("limit");
      this.$bvToast.hide("available");
    }
  }
  openSetting() {
    openApplicationSettings();
  }
  async fetchFirstBootSurvey() {
    const fcmToken = await getFcmToken();
    if (fcmToken) {
      await authStore.fetchFirstBootSurvey(fcmToken);
      await authStore.fetchFirstBootSurveyV2(fcmToken);
    }
  }
  async isAnsweredFirstBootSurvey() {
    await this.fetchFirstBootSurvey();
    return (
      authStore.firstBootSurvey !== null || authStore.firstBootSurveyV2 !== null
    );
  }

  // アプリ評価モーダルを閉じる
  closeAppReviewModal() {
    this.$bvModal.hide("app-review-modal");
  }

  async closeHookApplicationReminderModal(closeType: "confirm" | "cancel") {
    try {
      this.$loading.show();
      if (closeType === "confirm") {
        await applicationService.confirmApplicationReminder(
          this.applicationToRemind!.id
        );
        // キャンセルの場合は、ここではリマインダー確認したことを記録しない(application.isConfirmedをtrueにしない)
        // ここで記録すると、キャンセル画面でキャンセルせずに案件一覧画面へ戻った場合に
        // リマインダーが再度表示されなくなってしまうため
        // キャンセル時にリマインダーを確認したことを記録する
        this.applicationToRemind = null;
      }
    } catch (e) {
      captureException(e as Error);
    } finally {
      this.$loading.hide();
    }
  }

  showApplicationReminderModal(): void {
    if (!this.isLogin) return;

    const reminderApplication = applicationService.getReminderApplication();
    if (reminderApplication) {
      this.applicationToRemind = reminderApplication;
      // 前日確認モーダルが表示されるとき、アプリ評価モーダルは表示しない
      this.shouldShowAppSatisfactionModal = false;
    }
    if (this.applicationModalTimer) {
      clearInterval(this.applicationModalTimer);
    }
  }

  getEvaluation(order: SlimOrder) {
    if (!this.isLogin) {
      return null;
    }
    return this.evaluations[order.office.id] || null;
  }

  async fetchCountOfLimitedOrders() {
    // ログイン、基本情報登録をしていない場合は限定オファーは存在しないため処理をスキップ
    if (!this.isLogin || !this.hasRegisteredPersonalInfo) {
      return;
    }

    try {
      const { count } = await orderService.fetchCountOfLimitedOrders();
      this.existsLimitedOrders = count > 0;
    } catch (e) {
      captureException(e as Error);
    }
  }

  async fetchCountOfIndividualOfferOrders() {
    // ログイン、基本情報登録をしていない場合は指名オファーは存在しないため処理をスキップ
    if (!this.isLogin || !this.hasRegisteredPersonalInfo) {
      return;
    }

    try {
      const { count } = await orderService.fetchCountOfIndividualOfferOrders();
      this.existsIndividualOrders = count > 0;
    } catch (e) {
      captureException(e as Error);
    }
  }
}

export default toNative(Search);
</script>

<style lang="scss" scoped>
.search-page {
  min-height: 100vh;
}

.app-store-banner-space {
  margin-top: $app-store-banner-height;
}

.container {
  min-height: calc(100vh - env(safe-area-inset-top, 0px) - 110px - 80px);
  padding-right: 0;
  padding-left: 0;
}

.search-header {
  position: relative;
  top: 0;
  z-index: 1;
  padding-bottom: calc(env(safe-area-inset-top, 0px) + 110px + 40px);
}

.search-header-inner {
  position: fixed;
  left: 0;
  width: 100%;
  border-bottom: 1px solid $gray-03;
}

.search-header-notch {
  height: env(safe-area-inset-top, 0);
}

.search-header-items {
  display: flex;
  align-items: center;
  justify-content: right;
  height: 36px;
  padding-top: 12px;
  padding-bottom: 0;
  margin-right: 4px;

  .schedule-button {
    flex-grow: 1;
    margin-left: 8px;
    font-size: 14px;
  }

  :deep(.filter-button),
  :deep(.sort-button) {
    font-size: 14px;
  }

  :deep(svg) {
    width: 22px;
    height: 22px;
  }
}

.ad-slot-search {
  margin: 6px 8px 0;
}

.search-toaster {
  right: 0.5rem;
  left: 0.5rem;

  :deep(.b-toast) {
    width: 100%;
    max-width: 100%;
  }

  :deep(.toast) {
    width: 100%;
    max-width: 100%;
  }
}
</style>
